import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-app-entwicklung";
const TITLE = "Shopify App Entwicklung: wann eine App sinnvoll ist";
const DESCRIPTION =
  "Shopify App Entwicklung im Überblick: Custom App, Public App oder Theme-Anpassung, Aufwand, Kostenrahmen und Betrieb. Umsetzung durch einen Shopify Entwickler zu 110 € netto pro Stunde.";
const UPDATED = "2026-09-10";

const ANSWER =
  "Eine eigene Shopify App lohnt sich, wenn eine Funktion außerhalb des Themes laufen muss: Schnittstellen zu ERP oder Warenwirtschaft, eigene Admin-Oberflächen, automatisierte Prozesse oder Logik, die auf Webhooks reagiert. Alles, was nur die Darstellung im Shop betrifft, ist als Theme-Anpassung günstiger und wartungsärmer. Für den eigenen Shop reicht in der Regel eine Custom App statt einer App für den Shopify App Store.";

const faqs = [
  {
    q: "Wann brauche ich eine eigene Shopify App?",
    a: "Wenn Logik außerhalb der Shop-Darstellung laufen muss: Anbindung an ERP, Warenwirtschaft oder PIM, eigene Oberflächen im Shopify-Admin, geplante Aufgaben oder Reaktionen auf Webhooks. Reine Darstellungsänderungen gehören ins Theme.",
  },
  {
    q: "Was kostet die Entwicklung einer Shopify App?",
    a: "Kleine Custom Apps mit einer klaren Aufgabe liegen häufig bei 3.000 bis 8.000 € netto, Schnittstellenprojekte mit Datenabgleich deutlich darüber. Abgerechnet wird nach Aufwand zu 110 € netto pro Stunde, nach Sichtung gibt es einen festen Rahmen.",
  },
  {
    q: "Custom App oder Public App?",
    a: "Für den eigenen Shop reicht eine Custom App: schneller gebaut, kein App-Store-Review, volle Kontrolle über Datenzugriffe. Eine Public App lohnt nur, wenn die Funktion an andere Händler verkauft werden soll.",
  },
  {
    q: "App oder Theme-Anpassung?",
    a: "Wenn eine Funktion sichtbar im Shop stattfindet, etwa Produktkonfiguration, Bundles oder Anzeigelogik, ist die Theme-Anpassung meist günstiger, schneller und besser für die Ladezeit. Eine App ist die richtige Wahl bei Daten, Prozessen und Schnittstellen.",
  },
  {
    q: "Wer betreut die App nach dem Livegang?",
    a: "Shopify aktualisiert seine API-Versionen regelmäßig, deshalb braucht jede App gelegentliche Pflege. Ich übernehme Wartung und Erweiterungen nach Aufwand, ohne Vertragsbindung.",
  },
];

export const Route = createFileRoute("/shopify-app-entwicklung")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Entwicklung",
      updated: UPDATED,
      breadcrumb: { name: "Shopify App Entwicklung", path: PATH },
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <ArticlePage
      kicker="Leistung · Entwicklung"
      title="Shopify App Entwicklung: Custom App, Schnittstelle oder Theme"
      intro="Nicht jede Anforderung braucht eine eigene App. Hier steht, wann sich die Entwicklung lohnt, wann eine Theme-Anpassung günstiger ist, womit Sie beim Aufwand rechnen sollten und wie die Zusammenarbeit abläuft."
      source="shopify-app-entwicklung"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/leistungen", label: "Leistungen" }}
      sections={[
        {
          h: "Typische Aufgaben aus der Praxis",
          p: [
            "Die meisten Anfragen zur App-Entwicklung lassen sich in wenige Muster einordnen. Diese Einordnung entscheidet über Aufwand und Betriebskosten.",
          ],
          list: [
            "Anbindung an ERP, Warenwirtschaft oder Buchhaltung mit regelmäßigem Datenabgleich",
            "Automatisierte Prozesse über Webhooks, etwa bei Bestellungen oder Rückgaben",
            "Eigene Oberflächen im Shopify-Admin für interne Abläufe",
            "Datenexporte und Berichte, die Shopify von Haus aus nicht liefert",
            "Produktdatenpflege aus einem externen System",
          ],
        },
        {
          h: "Custom App statt App Store",
          p: [
            "Für einen einzelnen Shop ist eine Custom App der direkte Weg: Sie wird nur in Ihrem Shop installiert, braucht kein Review, und die Zugriffsrechte lassen sich eng auf das Nötige begrenzen.",
            "Eine App für den Shopify App Store ist ein eigenes Produkt mit Abrechnung, Support und Review-Prozess. Das lohnt nur, wenn Sie die Funktion an andere Händler verkaufen wollen.",
          ],
        },
        {
          h: "Wann die Theme-Anpassung die bessere Wahl ist",
          p: [
            "Sichtbare Funktionen im Shop, etwa Bundles, Produktkonfiguratoren, Anzeigelogik oder zusätzliche Felder auf der Produktseite, lassen sich fast immer direkt im Theme lösen. Das ist günstiger, schneller und belastet die Ladezeit weniger als eine zusätzliche App.",
            "Faustregel: Betrifft es die Darstellung, gehört es ins Theme. Betrifft es Daten, Prozesse oder externe Systeme, gehört es in eine App.",
          ],
        },
        {
          h: "Ablauf und Kostenrahmen",
          p: [
            "Nach einem kurzen Gespräch sichte ich Anforderung, bestehende Systeme und Datenwege und liefere eine Aufwandsschätzung mit fester Obergrenze. Umgesetzt wird in klar abgegrenzten Schritten mit Zwischenstand, damit Sie jederzeit stoppen oder umpriorisieren können.",
            "Abgerechnet wird nach Aufwand zu 110 € netto pro Stunde. Kleine Custom Apps mit einer klaren Aufgabe liegen häufig bei 3.000 bis 8.000 € netto, Schnittstellenprojekte darüber.",
          ],
        },
        {
          h: "Betrieb nach dem Livegang",
          p: [
            "Shopify aktualisiert seine API-Versionen regelmäßig. Deshalb plane ich für jede App eine gelegentliche Pflege ein, damit sie nach Versionswechseln weiterläuft. Wartung und Erweiterungen laufen nach Aufwand, ohne Vertragsbindung.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-entwickler", label: "Shopify Entwickler beauftragen" },
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung und Entwicklung" },
        { to: "/blog/shopify-apps-auswaehlen", label: "Shopify Apps auswählen" },
        { to: "/cases", label: "Kundenprojekte ansehen" },
        { to: "/kontakt", label: "Projekt anfragen" },
      ]}
    />
  );
}
